import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

export default function BeforeAfter() {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  return (
    <section
      data-testid="before-after"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ background: "#141014" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col items-center text-center mb-14 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-[var(--iw-gold)]/60" />
            <span className="overline text-[var(--iw-gold)]">The Difference</span>
            <span className="h-px w-10 bg-gradient-to-l from-transparent to-[var(--iw-gold)]/60" />
          </div>
          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-5xl md:text-6xl leading-[1.02] tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Ordinary invite, or{" "}
            <span className="italic gold-rose-gradient">Invitation Wala</span>?
          </h2>
          <p className="text-[var(--iw-muted)] mt-6 max-w-xl leading-relaxed">
            Drag the divider to compare. The left is a standard card most
            couples settle for. The right is what your family will screenshot.
          </p>
        </div>

        <div
          ref={wrapRef}
          className="relative w-full aspect-[16/10] md:aspect-[16/9] overflow-hidden border border-[var(--iw-gold)]/30 select-none cursor-ew-resize"
          onMouseMove={(e) => updateFromClientX(e.clientX)}
          onTouchMove={(e) => {
            if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
          }}
          onTouchStart={(e) => {
            if (e.touches[0]) updateFromClientX(e.touches[0].clientX);
          }}
          data-testid="before-after-wrap"
        >
          {/* BEFORE — ordinary */}
          <img
            src="https://images.unsplash.com/photo-1532712938310-34cb3982ef74?auto=format&fit=crop&w=1800&q=85"
            alt="Ordinary printed wedding card with plain typography — example of a traditional invite"
            className="absolute inset-0 w-full h-full object-cover grayscale contrast-75 brightness-90"
            draggable="false"
            loading="lazy"
            decoding="async"
          />
          <span className="absolute top-5 left-5 overline px-3 py-1 bg-black/60 text-[var(--iw-muted)] border border-white/15">
            Ordinary
          </span>

          {/* AFTER — Invitation Wala (clipped by slider) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/fnvfaxzf_IMG_3305.JPG.jpeg"
              alt="InvitationWala premium digital save-the-date with Mumbai Gateway illustration in gold and rose"
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
              loading="lazy"
              decoding="async"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,10,10,0.1) 0%, rgba(10,10,10,0.35) 100%)",
              }}
            />
            <span
              className="absolute top-5 left-5 overline px-3 py-1 border"
              style={{
                background: "rgba(212,175,55,0.15)",
                borderColor: "var(--iw-gold)",
                color: "var(--iw-gold)",
              }}
            >
              Invitation Wala
            </span>
          </div>

          {/* Slider line */}
          <div
            className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
            style={{
              left: `calc(${pos}% - 1px)`,
              background:
                "linear-gradient(to bottom, var(--iw-gold), var(--iw-rose))",
              boxShadow: "0 0 20px rgba(232,165,180,0.6)",
            }}
          />
          {/* Handle */}
          <motion.div
            animate={{ left: `${pos}%` }}
            transition={{ duration: 0 }}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full grid place-items-center"
            style={{
              background:
                "linear-gradient(135deg, var(--iw-gold), var(--iw-rose-deep))",
              boxShadow: "0 10px 40px -5px rgba(232,165,180,0.6)",
            }}
          >
            <MoveHorizontal size={18} className="text-black" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
